import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { Crest } from "@/components/Crest";
import { fmtNumber } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ENTITY } from "@/data/constants";

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { dataset, state, set } = useDemo();
  const profile = dataset.profiles[state.profileId];
  const club = dataset.clubsById[profile.favClubId];
  const [name, setName] = useState(profile.displayName);
  const [notif, setNotif] = useState({ match: true, result: true, weekly: false });

  const changeLang = (lng) => { set("language", lng); i18n.changeLanguage(lng); };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-5 space-y-5">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl grid place-items-center" style={{ background: club.primary, color: club.secondary }}>
          <Crest club={club} size={40} />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-wide">{t("profile.title")}</h1>
          <p className="text-neutral-500 text-sm">{club.name}</p>
        </div>
      </div>

      <Section title={t("profile.displayName")}>
        <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)}
          className="bg-neutral-900 border-neutral-800 max-w-sm" />
      </Section>

      <Section title={t("profile.favClub")}>
        <Select value={profile.favClubId} onValueChange={(v) => { /* presentational */ }}>
          <SelectTrigger data-testid="profile-club" className="bg-neutral-900 border-neutral-800 max-w-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-neutral-900 border-neutral-800 max-h-72">
            {dataset.clubs.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Section>

      <Section title={t("profile.language")}>
        <div className="flex gap-2">
          {[["en", "English"], ["de", "Deutsch"], ["es", "Español"]].map(([v, l]) => (
            <button key={v} data-testid={`profile-lang-${v}`} onClick={() => changeLang(v)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${state.language === v ? "bg-[#b6ff2e] text-black border-[#b6ff2e]" : "border-neutral-800 text-neutral-300"}`}>{l}</button>
          ))}
        </div>
      </Section>

      <Section title={t("profile.notifications")}>
        <div className="space-y-3 max-w-sm">
          <ToggleRow label={t("profile.notifMatch")} checked={notif.match} onChange={(v) => setNotif((n) => ({ ...n, match: v }))} testid="notif-match" />
          <ToggleRow label={t("profile.notifResult")} checked={notif.result} onChange={(v) => setNotif((n) => ({ ...n, result: v }))} testid="notif-result" />
          <ToggleRow label={t("profile.notifWeekly")} checked={notif.weekly} onChange={(v) => setNotif((n) => ({ ...n, weekly: v }))} testid="notif-weekly" />
        </div>
      </Section>

      <Section title={t("profile.history")}>
        {profile.history.length === 0 ? (
          <p className="text-neutral-500 text-sm">{t("profile.emptyHistory")}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {profile.history.map((h) => (
              <div key={h.matchday} className="px-2 py-1 rounded-md bg-neutral-900 border border-neutral-800 text-xs tabular">
                <span className="text-neutral-500">MD{h.matchday}</span>{" "}
                <span style={{ color: ENTITY.you.color }}>{fmtNumber(h.lgd, state.language, 2)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-4">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display mb-2">{title}</div>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-neutral-300">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
